import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../../services/auth/authService';

/**
 * Login page component.
 *
 * On mount, checks whether the user already has a valid session in
 * sessionStorage. If authenticated, redirects immediately to /home.
 * If not, initiates the PingFederate OAuth 2.0 PKCE login flow.
 *
 * Cross-tab logout path: ProtectedRoute writes `auth_forced_logout = '1'` to
 * sessionStorage when a logout broadcast is received from another tab.
 * When this flag is present we:
 *  1. Skip checkAndRefreshAuth (tokens are already cleared).
 *  2. Wait 1 500 ms before calling initiateLogin().
 *     Why: authService.logout() broadcasts the key before Tab A's 200 ms SLO
 *     redirect timer fires. Without the delay Tab B can reach PingFed while
 *     the SSO cookie is still valid and PingFed silently re-authenticates.
 *     The wait gives Tab A’s SLO request time to complete and the SSO cookie
 *     time to be invalidated, so PingFed shows the login page as expected.
 *
 * Does NOT call sessionStorage.clear() before initiateLogin — clearing
 * sessionStorage here would wipe the `postLoginRedirect` key that
 * ProtectedRoute wrote before redirecting here, breaking the return flow.
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Prevents double-invocation in React StrictMode.
   * Without this guard, two concurrent checkAndRefreshAuth calls can trigger
   * two simultaneous refresh requests, causing PingFed's token rotation to
   * invalidate the second request (invalid_grant).
   */
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    const checkAuth = async (): Promise<void> => {
      try {
        setIsChecking(true);

        // Cross-tab logout: ProtectedRoute set this flag after clearing the
        // session. Skip the auth check — tokens are already gone.
        // Wait 1 500 ms so Tab A’s PingFed SLO has time to invalidate the SSO
        // cookie before we redirect. Without the delay PingFed silently
        // re-authenticates using the still-valid SSO session cookie.
        const isForcedLogout = sessionStorage.getItem('auth_forced_logout') === '1';
        if (isForcedLogout) {
          sessionStorage.removeItem('auth_forced_logout');
          await new Promise<void>((resolve) => setTimeout(resolve, 2000));
          authService.initiateLogin();
          return;
        }

        const isAuthenticated = await authService.checkAndRefreshAuth();

        if (isAuthenticated) {
          // User has an active session — skip the login page entirely
          navigate('/home', { replace: true });
        } else {
          setIsChecking(false);
          // Do NOT clear sessionStorage here — postLoginRedirect must survive
          // the round-trip through PingFed so AuthCallback can redirect correctly
          authService.initiateLogin();
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
        setError('Authentication failed. Please try again.');
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isChecking) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Checking authentication status…</h2>
        <p>Please wait while we verify your session.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <h2>Redirecting to login…</h2>
      <p>Please wait while we redirect you to the authentication page.</p>
    </div>
  );
};

export default Login;